import type { CurrencyCode } from "@/models/currencyCode"
import type { DisplayValueMap, Unit, UnitSeparator } from "@/models/unit"
import {
  UnitName,
  languageToUnitMap,
  unitSeparators,
  units,
} from "@/models/unit"

import { DEFAULT_CURRENCY } from "./constants"
import { getCurrencySymbol } from "./currency"

interface GetUnitProps {
  unitName: UnitName
  unitsSystem?: string
}

const getUnit = ({ unitName, unitsSystem }: GetUnitProps): Unit | undefined => {
  const unit: Unit | undefined = units.get(unitName)

  if (!unit || !Object.values(UnitName).includes(unitName)) {
    return
  }

  if (unitName.includes("currency")) {
    return units.get(UnitName.Currency)
  }

  if (!unitsSystem) {
    return unit
  }

  // Get correct unit based on locale
  // Try full locale code first, then fall back to language code (e.g., "ja-JP" -> "ja")
  const unitMap =
    languageToUnitMap.get(unitsSystem) ??
    languageToUnitMap.get(unitsSystem.split("-")[0])

  const mappedUnitName = unitMap?.get(unitName) ?? unitName

  return units.get(mappedUnitName)
}

const isTokenASeparator = (token: string): boolean =>
  Array.from(unitSeparators.values())
    .map((separator) => separator.tokenValue)
    .includes(token)

const tokenizeUnit = (unit: UnitName): string[] => {
  const tokenizedStr: string = Array.from(unitSeparators.values()).reduce(
    (value: string, separator): string =>
      value.split(separator.tokenValue).join(` ${separator.tokenValue} `),
    unit
  )

  // trim() to handle Unit.PerKilowattHour where -per- appears as the first token
  return tokenizedStr.trim().split(" ")
}

interface TranslateUnitPartProps {
  currencyCode: CurrencyCode
  localeCode: string
  unit: Unit | undefined
  value: number | string | null | undefined
}

const translateUnitPart = ({
  currencyCode,
  localeCode,
  unit,
  value,
}: TranslateUnitPartProps): string => {
  if (!unit) {
    return ""
  }

  // Handle currency
  if (unit.id === UnitName.Currency) {
    return getCurrencySymbol(localeCode, currencyCode)
  }

  // Handle custom units
  if (!unit.isECMAScriptSupported) {
    return unit.displayValue[localeCode as keyof DisplayValueMap]
      .shortDisplayValue
  }

  // Handle Intl.NumberFormat supported units
  const translatedNumberParts = Intl.NumberFormat(localeCode, {
    style: "unit",
    unit: unit.tokenValue,
  }).formatToParts(Number(value) || 0)

  return translatedNumberParts.find((part) => part.type === "unit")?.value ?? ""
}

interface TranslateUnitProps {
  currencyCode?: CurrencyCode
  localeCode: string
  unit: UnitName | null | undefined
  unitsSystem?: string
  value?: number | string | null
}

/**
 * A function that formats and internationalizes the provided unit
 *
 * @param props - The properties object
 * @param props.currencyCode - The currency code
 * @param props.localeCode - The locale code
 * @param props.unit - The unit
 * @param props.unitsSystem - The units system code. When set, remaps units such as Gallon→L and MMBtu→m³.
 * @param props.value - The number to use for handling pluralization
 * @example
 * translateUnit({ localeCode: i18n.language, unit: SIUnit.KilowattHour, unitsSystem: userSettings.unitsSystem, currencyCode: userSettings.currency, value: 123 })
 */
export const translateUnit = ({
  localeCode,
  unit,
  unitsSystem,
  currencyCode = DEFAULT_CURRENCY,
  value,
}: TranslateUnitProps): string => {
  if (!unit) {
    return ""
  }

  // Log invalid units so we'll see them in our observability tools
  if (!Object.values(UnitName).includes(unit)) {
    console.error(`${unit} is not a supported unit`)
    return ""
  }

  const tokens: string[] = tokenizeUnit(unit)

  const unitDisplays: string[] = tokens.map((token) => {
    if (isTokenASeparator(token)) {
      const separator: UnitSeparator | undefined = Array.from(
        unitSeparators.values()
      ).find((unitSeparator) => unitSeparator.tokenValue === token)

      return separator?.displayValue ?? ""
    }

    return translateUnitPart({
      currencyCode,
      localeCode,
      unit: getUnit({
        unitName: token as UnitName,
        unitsSystem: unitsSystem,
      }),
      value,
    })
  })

  // trim() to handle Unit.PerKilowattHour where -per- appears as the first token
  return unitDisplays.join("").trim()
}

/**
 * Server usage strings that match a known unit id. Keys are lowercase.
 * Unlisted strings stay as the server sent them.
 */
const serverUsageUnitNames = new Map<string, UnitName>([
  ["btu", UnitName.BritishThermalUnit],
  ["ccf", UnitName.CentumCubicFeet],
  ["cubic_meters", UnitName.CubicMeter],
  ["day", UnitName.Day],
  ["decatherms", UnitName.Decatherm],
  ["gal", UnitName.Gallon],
  ["gallon", UnitName.Gallon],
  ["gallons", UnitName.Gallon],
  ["kg", UnitName.Kilogram],
  ["kgal", UnitName.KiloGallon],
  ["kw", UnitName.Kilowatt],
  ["kwh", UnitName.KilowattHour],
  ["l", UnitName.Liter],
  ["liter", UnitName.Liter],
  ["m3", UnitName.CubicMeter],
  ["mcf", UnitName.MilleCubicFeet],
  ["mmbtu", UnitName.MetricMillionBritishThermalUnits],
  ["mwh", UnitName.MegawattHour],
  ["tgal", UnitName.ThousandGallon],
  ["therm", UnitName.Therm],
  ["therms", UnitName.Therm],
  ["wh", UnitName.WattHour],
])

export const getUnitNameForServerUnit = (
  serverUnit: string | null | undefined
): UnitName | undefined => {
  const normalized = serverUnit?.trim().toLowerCase() ?? ""
  if (normalized === "") {
    return undefined
  }

  return serverUsageUnitNames.get(normalized)
}

const unitNameIds = new Set<string>(Object.values(UnitName))

export const resolveUnitName = (
  unit: string | null | undefined
): UnitName | undefined => {
  const trimmed = unit?.trim() ?? ""
  if (trimmed === "") {
    return undefined
  }

  if (unitNameIds.has(trimmed)) {
    return trimmed as UnitName
  }

  return getUnitNameForServerUnit(trimmed)
}

/** The `-per-` form of a base unit, when the catalog defines one. */
export const toPerUnitName = (
  unit: UnitName | undefined
): UnitName | undefined => {
  if (unit == null) {
    return undefined
  }

  const perUnit = `-per-${unit}`
  if (!unitNameIds.has(perUnit)) {
    return undefined
  }

  return perUnit as UnitName
}
